/**
 * PILGRIM media toggle
 * Pins every product card on the page to either its primary (packshot) image or
 * its second (model) image by setting each card-gallery slideshow's
 * `initial-slide` attribute. The slideshow honours that both immediately (its
 * attributeChangedCallback re-selects the slide) and during its own multi-frame
 * initialisation (it positions to the initial slide instead of snapping back to
 * slide 0) — so a freshly rendered card from a Load More batch lands on the
 * chosen image without us having to out-race the slideshow's async init.
 *
 * Performance (2026-09-16): the slideshow's select() writes `aria-hidden` on a
 * slide and then reads a computed style, which forces a style recalculation per
 * card. Chromium recalculates the WHOLE document on any aria-hidden change
 * (native Horizon rules combine `:has()` with `:not([aria-hidden])`, which
 * Blink cannot scope), so 48 cards cost ~550 ms in one blocking task — several
 * seconds on a slow Windows laptop, while WebKit needed ~160 ms. Two measures:
 * 1. Before pinning a card, write the end-state aria-hidden values ourselves.
 *    The slideshow's own writes then carry the same value, which costs nothing.
 *    Measured: 538 ms → 33 ms to first paint in Chromium.
 * 2. Cards are pinned in per-frame batches, visible cards first, so even a
 *    slow machine paints the switch and the visible cards straight away.
 *
 * Requires the model image to be rendered as a slide, i.e. theme setting
 * `show_second_image_on_hover` enabled; single-image cards have no model slide
 * and are skipped. The choice is persisted in localStorage so it survives
 * navigation between collections.
 *
 * Scope (2026-10-17): `data-scope="page"` (the collection toolbar) drives the
 * page's `results-list` and remembers the choice under one site-wide key.
 * `data-scope="list"` (Product list (PILGRIM)) drives only the enclosing
 * <pilgrim-product-list> and remembers the choice under that list's own key
 * (`data-storage-key`), so a list never changes collection pages and vice
 * versa. `data-default-mode` is what shows until the visitor has a choice.
 */
const STORAGE_KEY = 'pilgrim:media-mode';

// Media render as [featured, ...rest], so the second image (the model shot) is
// slide index 1; the packshot is slide 0.
const MODEL_SLIDE_INDEX = 1;
const PACKSHOT_SLIDE_INDEX = 0;

// Cards pinned per animation frame after the first batch (the first batch is
// every card currently in the viewport, however many there are).
const BATCH_SIZE = 12;

class PilgrimMediaToggle extends HTMLElement {
  /** Bumped on every mode change and on disconnect; a stale batch loop stops. */
  #run = 0;

  connectedCallback() {
    this.addEventListener('change', this._onChange);
    this._observeGrid();
    this._restoreMode();
  }

  disconnectedCallback() {
    this.#run++;
    this.removeEventListener('change', this._onChange);
    this._observer?.disconnect();
  }

  _onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.type !== 'checkbox') return;

    const mode = input.checked ? 'model' : 'packshot';
    this._persistMode(mode);
    const grid = this._getGrid();
    if (grid) grid.dataset.pilgrimMediaMode = mode;
    this._applyMode(mode);
  };

  get _storageKey() {
    if (this.dataset.scope === 'list') return this.dataset.storageKey || null;
    return STORAGE_KEY;
  }

  /** The visitor's remembered choice, else the merchant's default. */
  _restoreMode() {
    let mode = null;
    const key = this._storageKey;
    if (key) {
      try {
        mode = localStorage.getItem(key);
      } catch {
        /* storage blocked — fall back to the default */
      }
    }
    if (mode !== 'model' && mode !== 'packshot') {
      mode = this.dataset.defaultMode === 'model' ? 'model' : 'packshot';
    }

    const checkbox = this.querySelector('input[type="checkbox"]');
    if (checkbox instanceof HTMLInputElement) {
      checkbox.checked = mode === 'model';
    }

    if (mode === 'model') {
      // Wait one frame so the grid's card-galleries are present in the DOM.
      requestAnimationFrame(() => {
        const grid = this._getGrid();
        if (grid) grid.dataset.pilgrimMediaMode = mode;
        this._applyMode(mode);
      });
    }
  }

  _persistMode(mode) {
    const key = this._storageKey;
    if (!key) return;
    try {
      localStorage.setItem(key, mode);
    } catch {
      /* private browsing or quota — silently ignore */
    }
  }

  /** The element whose product cards this switch drives. */
  _getGrid() {
    if (this.dataset.scope === 'list') return this.closest('pilgrim-product-list');
    return document.querySelector('results-list');
  }

  _observeGrid() {
    const grid = this._getGrid();
    if (!grid) return;
    this._observer = new MutationObserver((mutations) => {
      if (grid.dataset.pilgrimMediaMode !== 'model') return;

      const galleries = [];
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('.card-gallery')) {
            galleries.push(node);
          } else {
            node.querySelectorAll?.('.card-gallery').forEach((gallery) => galleries.push(gallery));
          }
        }
      }
      // Pinned synchronously, in the same microtask as the insertion: a card
      // pinned a frame later can land while its slideshow is mid-initialisation
      // and stay on slide 0 (seen 2026-09-16: 12 of 48 Load More cards). With
      // the aria-hidden preset a whole batch costs ~1 ms per card anyway.
      for (const gallery of galleries) this._pinGallery(gallery, MODEL_SLIDE_INDEX);
    });
    this._observer.observe(grid, { childList: true, subtree: true });
  }

  _applyMode(mode) {
    const index = mode === 'model' ? MODEL_SLIDE_INDEX : PACKSHOT_SLIDE_INDEX;
    const grid = this._getGrid();
    if (!grid) return;
    const galleries = Array.from(grid.querySelectorAll('.card-gallery'));
    this._pinGalleries(galleries, index, ++this.#run);
  }

  /**
   * Pin galleries in per-frame batches: every gallery whose slideshow is in the
   * viewport (Horizon's own `in-viewport` marker) in the first frame, then
   * BATCH_SIZE per frame. Stops as soon as `run` is no longer the current one.
   * @param {Element[]} galleries
   * @param {number} index
   * @param {number} run
   */
  _pinGalleries(galleries, index, run) {
    const inViewport = (gallery) => gallery.querySelector('slideshow-component')?.hasAttribute('in-viewport') ?? false;
    const visible = galleries.filter(inViewport);
    const rest = galleries.filter((gallery) => !inViewport(gallery));
    const batches = [visible];
    for (let i = 0; i < rest.length; i += BATCH_SIZE) batches.push(rest.slice(i, i + BATCH_SIZE));

    const step = () => {
      if (run !== this.#run) return;
      const batch = batches.shift();
      if (!batch) return;
      for (const gallery of batch) this._pinGallery(gallery, index);
      if (batches.length) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /**
   * Point a card-gallery's slideshow at the given slide via `initial-slide`.
   * When pinning to the model image, galleries with no second slide (single-
   * image products) are left untouched.
   * @param {Element} gallery
   * @param {number} index
   */
  _pinGallery(gallery, index) {
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow) return;
    const slides = gallery.querySelectorAll('slideshow-slide');
    if (index > 0 && slides.length <= index) return;

    // End-state aria-hidden first (see the header comment): the target slide
    // becomes visible, every other slide the slideshow manages (not the hidden
    // variant-image slides it skips) becomes hidden. Same values as the
    // slideshow itself writes, so its writes no longer force a recalc.
    slides.forEach((slide, i) => {
      if (i !== index && slide.hasAttribute('hidden') && !slide.hasAttribute('reveal')) return;
      const value = i === index ? 'false' : 'true';
      if (slide.getAttribute('aria-hidden') !== value) slide.setAttribute('aria-hidden', value);
    });

    slideshow.setAttribute('initial-slide', String(index));
  }
}

if (!customElements.get('pilgrim-media-toggle')) {
  customElements.define('pilgrim-media-toggle', PilgrimMediaToggle);
}
